// Geometry helpers for cutting map features into tiles. Points are flat
// arrays [x0, z0, x1, z1, ...].

export function polygonArea(p) {
  let a = 0;
  for (let i = 0, n = p.length; i < n; i += 2) {
    const j = (i + 2) % n;
    a += p[i] * p[j + 1] - p[j] * p[i + 1];
  }
  return a / 2;
}

export function centroid(p) {
  let x = 0;
  let z = 0;
  const n = p.length / 2;
  for (let i = 0; i < p.length; i += 2) {
    x += p[i];
    z += p[i + 1];
  }
  return [x / n, z / n];
}

export function polylineLength(p) {
  let len = 0;
  for (let i = 2; i < p.length; i += 2) len += Math.hypot(p[i] - p[i - 2], p[i + 1] - p[i - 1]);
  return len;
}

// Clips one segment to a rectangle (Liang-Barsky). Returns [ax, az, bx, bz] or null.
export function clipSegment(ax, az, bx, bz, x0, z0, x1, z1) {
  const dx = bx - ax;
  const dz = bz - az;
  let t0 = 0;
  let t1 = 1;
  const edges = [[-dx, ax - x0], [dx, x1 - ax], [-dz, az - z0], [dz, z1 - az]];
  for (const [p, q] of edges) {
    if (p === 0) {
      if (q < 0) return null;
    } else {
      const r = q / p;
      if (p < 0) {
        if (r > t1) return null;
        if (r > t0) t0 = r;
      } else {
        if (r < t0) return null;
        if (r < t1) t1 = r;
      }
    }
  }
  return [ax + t0 * dx, az + t0 * dz, ax + t1 * dx, az + t1 * dz];
}

// Clips a polyline to a rectangle. Returns a list of polylines (the line may
// leave and re-enter the rectangle).
export function clipPolyline(p, x0, z0, x1, z1) {
  const out = [];
  let cur = null;
  for (let i = 2; i < p.length; i += 2) {
    const s = clipSegment(p[i - 2], p[i - 1], p[i], p[i + 1], x0, z0, x1, z1);
    if (!s || (s[0] === s[2] && s[1] === s[3])) {
      cur = null;
      continue;
    }
    if (cur && cur[cur.length - 2] === s[0] && cur[cur.length - 1] === s[1]) {
      cur.push(s[2], s[3]);
    } else {
      cur = [s[0], s[1], s[2], s[3]];
      out.push(cur);
    }
  }
  return out;
}

// Clips a polygon to a rectangle (Sutherland-Hodgman). Returns a flat point
// array, empty when nothing is left.
export function clipPolygon(p, x0, z0, x1, z1) {
  let pts = p;
  const sides = [
    [(x) => x >= x0, 0, x0],
    [(x) => x <= x1, 0, x1],
    [(z) => z >= z0, 1, z0],
    [(z) => z <= z1, 1, z1],
  ];
  for (const [inside, axis, bound] of sides) {
    if (pts.length < 6) return [];
    const next = [];
    for (let i = 0, n = pts.length; i < n; i += 2) {
      const j = (i + 2) % n;
      const ax = pts[i];
      const az = pts[i + 1];
      const bx = pts[j];
      const bz = pts[j + 1];
      const aIn = inside(axis === 0 ? ax : az);
      const bIn = inside(axis === 0 ? bx : bz);
      if (aIn !== bIn) {
        const t = axis === 0 ? (bound - ax) / (bx - ax) : (bound - az) / (bz - az);
        const ix = axis === 0 ? bound : ax + t * (bx - ax);
        const iz = axis === 1 ? bound : az + t * (bz - az);
        if (aIn) next.push(ax, az, ix, iz);
        else next.push(ix, iz);
      } else if (aIn) {
        next.push(ax, az);
      }
    }
    // the loop above emits each inside start point; drop exact repeats
    pts = [];
    for (let i = 0; i < next.length; i += 2) {
      const k = pts.length;
      if (k >= 2 && pts[k - 2] === next[i] && pts[k - 1] === next[i + 1]) continue;
      pts.push(next[i], next[i + 1]);
    }
  }
  return pts.length >= 6 ? pts : [];
}

export function pointInPolygon(x, z, p) {
  let inside = false;
  for (let i = 0, n = p.length, j = n - 2; i < n; j = i, i += 2) {
    const xi = p[i];
    const zi = p[i + 1];
    const xj = p[j];
    const zj = p[j + 1];
    if (zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi) + xi) inside = !inside;
  }
  return inside;
}
